import { AbstractPlayer } from './abstract-player'
import {
    IEmbedConfig,
    IJWMedia,
    IJWPAdvertising,
    IJWPBiddersProviders,
    IJWPbids,
    IJWPSponsorshipAdTargeting,
    IJWPVideoAdTargeting,
    JWPlayerConfigEvent,
    VideoProviderShortType
} from '../types'
import { getAlphaNumericRandomStr } from 'utils/string'
import { objectToString } from 'utils/object'

/**
 * The common behaviour for JW player -- Player & Strategy Player.
 */
export abstract class AbstractJWPlayer extends AbstractPlayer {
    protected loc: string
    protected embedConfig: IEmbedConfig

    /* eslint-disable no-magic-numbers */
    private PLAYER_AD_SIZES = [[640, 480]]

    private adsSettings:
        | {
              disableBidding: boolean
              bidders: Array<IJWPBiddersProviders>
              floorPriceCents: number
              sendAllBids: boolean
          }
        | undefined

    protected get isContextualPlaylist(): boolean {
        return this.playlistType === 'contextual-playlist'
    }

    public constructor(
        protected element: HTMLElement,
        protected events: JWPlayerConfigEvent = {},
        protected isLeadVideo = false
    ) {
        super(element, events, isLeadVideo)
        const pageData = window.document.getElementById('page-data')
        if (pageData) {
            this.adsSettings = JSON.parse(
                pageData.innerHTML
            ).ads.jwPlayerAdSettings
        }
        this.loc = this.element.dataset.loc || ''
    }

    /**
     * convert the nest array of player sizes to a string
     */
    private convertPlayerSizesToString(
        adSizes: number[][],
        delimiter = 'x',
        divider = '%7C'
    ): string {
        return adSizes
            .map((item: number[]) => `${item[0]}${delimiter}${item[1]}`)
            .join(`${divider}`)
    }

    protected getAdvertisingConfigData(
        adTargets: IEmbedConfig,
        adScheduleId?: string,
        videoLoc?: string,
        adTargetingParams: Partial<IJWPVideoAdTargeting> = {},
        extraCustParams = {}
    ): IJWPAdvertising {
        const alphaNumericGenLength = 8
        const loc = videoLoc || '1'

        /* eslint-enable no-magic-numbers */
        const playerAdscheduleId = getAlphaNumericRandomStr(
            alphaNumericGenLength
        )

        // Video targeting params from FEM
        const custParams =
            adTargets.adsConfig?.adTagParameters?.cust_params || ''
        const iu = adTargets.adsConfig?.adTagParameters?.iu || ''
        const ppid = adTargets.adsConfig?.adTagParameters?.ppid || ''

        // Custom params for sponsorship support
        const sponsorshipAdTag: IJWPSponsorshipAdTargeting = {
            sensitive_video: '__item-sensitive_video__',
            vid: '__item-mediaid__',
            player: VideoProviderShortType.JwPlayer,
            loc,
            permutive: encodeURIComponent(
                JSON.parse(localStorage._pdfps || '[]').join(',')
            ),
            ...extraCustParams
        }

        const custSponsorshipParams = `%26${objectToString<IJWPSponsorshipAdTargeting>(
            sponsorshipAdTag,
            '%3D',
            '%26'
        )}`

        const plcmt = this.autoplay ? 2 : 1

        const adTargeting: IJWPVideoAdTargeting = {
            iu,
            ppid,
            plcmt,
            description_url: '__page-url__',
            sz: this.convertPlayerSizesToString(this.PLAYER_AD_SIZES),
            cmsid: '2568351',
            vid: '__item-mediaid__',
            gdfp_req: '1',
            output: 'vast',
            unviewed_position_start: '1',
            env: 'vp',
            tfcd: '0',
            impl: 's',
            correlator: '',
            cust_params: `${custParams}${custSponsorshipParams}`,
            ...adTargetingParams
        }

        const videoAdTag = `${'https://pubads.g.doubleclick.net/gampad/ads'}?${objectToString<IJWPVideoAdTargeting>(
            adTargeting
        )}&vid_d=__integer-duration__`

        let bids: IJWPbids | undefined
        if (
            this.adsSettings &&
            this.adsSettings.bidders.length > 0 &&
            !this.adsSettings.disableBidding
        ) {
            bids = {
                bidders: this.adsSettings.bidders || [],
                settings: {
                    sendAllBids: this.adsSettings.sendAllBids || false,
                    mediationLayerAdServer: 'dfp',
                    floorPriceCents: this.adsSettings.floorPriceCents,
                    buckets: [
                        {
                            min: 0.01,
                            max: 10,
                            increment: 0.01
                        },
                        {
                            min: 10,
                            max: 20,
                            increment: 0.02
                        },
                        {
                            min: 20,
                            max: 50,
                            increment: 0.5
                        }
                    ]
                },
                ortbParams: {
                    plcmt
                }
            }
        }

        return {
            client: 'googima',
            adscheduleid: adScheduleId || `${playerAdscheduleId}`,
            schedule: videoAdTag,
            bids
        }
    }

    /**
     * Get media metadata
     *
     * https://developer.jwplayer.com/jwplayer/reference/get_v2-media-media-id
     * @param id, ex: wYiMWUhb
     */
    protected async getJWMediaMetadata(): Promise<IJWMedia | void> {
        const id = this.videoId
        // Use proper media object API endpoint
        // https://developer.jwplayer.com/jwplayer/reference/playlists
        // https://developer.jwplayer.com/jwplayer/reference/media
        const endpoint =
            this.isLeadVideo || this.isContextualPlaylist
                ? 'playlists'
                : 'media'
        return fetch(`https://cdn.jwplayer.com/v2/${endpoint}/${id}`)
            .then(async (res) => {
                return res.json()
            })
            .catch((err) => console.error(err))
    }

    /**
     * Handle monetization configuration
     * based on monetize metadata flag
     *
     * @param IJWMedia
     */
    protected isMonetize(data: IJWMedia | void): boolean {
        return data &&
            data['playlist'] &&
            data['playlist'][0]['monetize'] === 'false'
            ? false
            : true
    }

    /**
     * Not used from JW Player
     */
    cuePlaylist(): void {
        return
    }
}
